import { useEffect, useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, DollarSign, TrendingUp, Clock, ShoppingCart, Activity, Calendar, Filter, RefreshCw, Wallet, UserCheck, Settings, Download, ChevronDown, ChevronRight, AlertCircle, CheckCircle2, Search, X } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

// Format INR revenue with adaptive units: < 1k shows full, < 1L shows in K, < 1Cr in L, else Cr.
const formatRevenue = (amount) => {
  const v = Number(amount) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(2)}`;
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [showAggregateDialog, setShowAggregateDialog] = useState(false);
  const [aggregateForm, setAggregateForm] = useState({ total_orders_historical: 0, total_revenue_historical: 0 });
  
  // Filter states
  const [dateRange, setDateRange] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState('all');
  
  // Custom date range for overview
  const [overviewDateRange, setOverviewDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Partner Breakdown table state
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownRange, setBreakdownRange] = useState('mtd'); // mtd | month | quarter | custom
  const [breakdownStart, setBreakdownStart] = useState('');
  const [breakdownEnd, setBreakdownEnd] = useState('');
  const [onlyWithRevenue, setOnlyWithRevenue] = useState(false);
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [breakdownExporting, setBreakdownExporting] = useState(false);
  const [breakdownExportingCsv, setBreakdownExportingCsv] = useState(false);
  const [dormantDays, setDormantDays] = useState(60);
  const [breakdownSearch, setBreakdownSearch] = useState('');

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [dateRange, selectedPartner]);

  useEffect(() => {
    fetchOverview();
  }, [overviewDateRange, customStartDate, customEndDate]);

  // Compute breakdown date range
  const getBreakdownDates = () => {
    const now = new Date();
    let start = null;
    let end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();
    switch (breakdownRange) {
      case 'mtd':
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        break;
      case 'month':
        const m = new Date(); m.setDate(m.getDate() - 30);
        start = m.toISOString();
        break;
      case 'quarter':
        const q = new Date(); q.setDate(q.getDate() - 90);
        start = q.toISOString();
        break;
      case 'all':
        start = null; end = null;
        break;
      case 'custom':
        if (breakdownStart) start = new Date(breakdownStart).toISOString();
        if (breakdownEnd) end = new Date(breakdownEnd + 'T23:59:59').toISOString();
        break;
      default:
        break;
    }
    return { start, end };
  };

  const fetchBreakdown = async () => {
    try {
      setBreakdownLoading(true);
      const { start, end } = getBreakdownDates();
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      if (onlyWithRevenue) params.append('only_with_revenue', 'true');
      params.append('dormant_days', String(dormantDays || 60));
      const url = `${API}/admin/dashboard/partner-breakdown?${params}`;
      const response = await fetch(url, { headers: getAuthHeaders(), cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to load partner breakdown');
      const data = await response.json();
      setBreakdown(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load partner breakdown');
    } finally {
      setBreakdownLoading(false);
    }
  };

  useEffect(() => {
    fetchBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdownRange, breakdownStart, breakdownEnd, onlyWithRevenue, dormantDays]);

  const _triggerDownload = (path, filename, setBusy) => {
    setBusy(true);
    const { start, end } = getBreakdownDates();
    const params = new URLSearchParams();
    if (start) params.append('start_date', start);
    if (end) params.append('end_date', end);
    if (onlyWithRevenue) params.append('only_with_revenue', 'true');
    params.append('dormant_days', String(dormantDays || 60));
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    const url = `${API}${path}?${params}`;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function () {
      setBusy(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(xhr.response);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`${filename.split('.').pop().toUpperCase()} downloaded`);
      } else {
        toast.error('Export failed');
      }
    };
    xhr.onerror = () => { setBusy(false); toast.error('Network error during export'); };
    xhr.send();
  };

  const handleExportBreakdown = () => {
    const today = new Date().toISOString().slice(0, 10);
    _triggerDownload('/admin/dashboard/partner-breakdown/export',
      `KiloBeaters_Partner_Breakdown_${today}.xlsx`, setBreakdownExporting);
  };

  const handleExportBreakdownCsv = () => {
    const today = new Date().toISOString().slice(0, 10);
    _triggerDownload('/admin/dashboard/partner-breakdown/export-csv',
      `KiloBeaters_Referral_Breakdown_${today}.csv`, setBreakdownExportingCsv);
  };

  // Client-side filtered rows for the breakdown table based on search
  const filteredBreakdownRows = (() => {
    const rows = breakdown?.rows || [];
    const q = breakdownSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(q)
      || (r.email || '').toLowerCase().includes(q)
      || (r.referral_code || '').toLowerCase().includes(q)
    );
  })();

  const getOverviewDateRange = () => {
    const now = new Date();
    let start_date = null;
    let end_date = now.toISOString();

    switch (overviewDateRange) {
      case 'today':
        start_date = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        end_date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start_date = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()).toISOString();
        end_date = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59).toISOString();
        break;
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        start_date = weekAgo.toISOString();
        break;
      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        start_date = monthAgo.toISOString();
        break;
      case 'mtd': // Month to Date
        start_date = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        break;
      case 'last_month': // Last Month
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        start_date = lastMonthStart.toISOString();
        end_date = lastMonthEnd.toISOString();
        break;
      case 'quarter':
        const quarterAgo = new Date();
        quarterAgo.setMonth(quarterAgo.getMonth() - 3);
        start_date = quarterAgo.toISOString();
        break;
      case 'year':
        const yearAgo = new Date();
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        start_date = yearAgo.toISOString();
        break;
      case 'custom':
        if (customStartDate) start_date = new Date(customStartDate).toISOString();
        if (customEndDate) end_date = new Date(customEndDate + 'T23:59:59').toISOString();
        break;
      default:
        start_date = null;
        end_date = null;
    }

    return { start_date, end_date };
  };

  const fetchOverview = async () => {
    try {
      const { start_date, end_date } = getOverviewDateRange();
      let url = `${API}/admin/dashboard/overview`;
      const params = new URLSearchParams();
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
        // Also update aggregate form with current values
        setAggregateForm({
          total_orders_historical: data.orders?.historical || 0,
          total_revenue_historical: data.revenue?.historical || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    }
  };

  const handleSaveAggregates = () => {
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/dashboard/set-aggregates`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        toast.success('Historical data saved!');
        setShowAggregateDialog(false);
        fetchOverview();
      } else {
        toast.error('Failed to save data');
      }
    };
    xhr.onerror = function() {
      toast.error('Network error');
    };
    xhr.send(JSON.stringify(aggregateForm));
  };

  const fetchPartners = async () => {
    try {
      const response = await fetch(`${API}/admin/partners/list`, {
        headers: getAuthHeaders(),
        
      });
      if (response.ok) {
        const data = await response.json();
        setPartners(data);
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  };

  const getDateRange = () => {
    const now = new Date();
    let start_date = null;
    let end_date = now.toISOString();

    switch (dateRange) {
      case 'today':
        start_date = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        break;
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        start_date = weekAgo.toISOString();
        break;
      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        start_date = monthAgo.toISOString();
        break;
      case 'quarter':
        const quarterAgo = new Date();
        quarterAgo.setMonth(quarterAgo.getMonth() - 3);
        start_date = quarterAgo.toISOString();
        break;
      case 'year':
        const yearAgo = new Date();
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        start_date = yearAgo.toISOString();
        break;
      default:
        return { start_date: null, end_date: null };
    }

    return { start_date, end_date };
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const { start_date, end_date } = getDateRange();
      
      let url = `${API}/admin/dashboard/stats?`;
      const params = new URLSearchParams();
      
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      if (selectedPartner !== 'all') params.append('partner_id', selectedPartner);
      
      const response = await fetch(`${url}${params.toString()}`, {
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to fetch stats');
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setDateRange('all');
    setSelectedPartner('all');
  };

  const statCards = [
    {
      title: 'Total Partners',
      value: stats?.total_partners || 0,
      subtitle: `${stats?.active_partners || 0} active`,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Total Revenue',
      value: `₹${stats?.total_revenue?.toFixed(2) || '0.00'}`,
      subtitle: `${stats?.total_transactions || 0} transactions`,
      icon: ShoppingCart,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Total Commission',
      value: `₹${stats?.total_commission?.toFixed(2) || '0.00'}`,
      subtitle: `${stats?.total_referrals || 0} referrals`,
      icon: DollarSign,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      title: 'Pending Payouts',
      value: stats?.pending_payouts || 0,
      subtitle: 'Awaiting approval',
      icon: Clock,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">System overview and statistics</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { fetchStats(); fetchOverview(); }} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showAggregateDialog} onOpenChange={setShowAggregateDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Set Historical Data
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Set Historical Aggregate Data</DialogTitle>
                <DialogDescription>
                  Enter historical data that predates the system. This will be added to system-tracked metrics.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Historical Orders (before system tracking)</Label>
                  <Input
                    type="number"
                    value={aggregateForm.total_orders_historical}
                    onChange={(e) => setAggregateForm({ ...aggregateForm, total_orders_historical: parseInt(e.target.value) || 0 })}
                    placeholder="e.g., 500"
                  />
                </div>
                <div>
                  <Label>Historical Revenue ₹ (before system tracking)</Label>
                  <Input
                    type="number"
                    value={aggregateForm.total_revenue_historical}
                    onChange={(e) => setAggregateForm({ ...aggregateForm, total_revenue_historical: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g., 500000"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAggregateDialog(false)}>Cancel</Button>
                <Button onClick={handleSaveAggregates}>Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Key Overview Metrics */}
      <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-orange-500" />
                Key Overview Metrics
              </CardTitle>
              <CardDescription>Core business metrics at a glance</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={overviewDateRange} onValueChange={setOverviewDateRange}>
                <SelectTrigger className="w-[160px] bg-white">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="mtd">Month to Date</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="quarter">Last 3 Months</SelectItem>
                  <SelectItem value="year">Last 12 Months</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              {overviewDateRange === 'custom' && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-[140px] bg-white"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-[140px] bg-white"
                  />
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <Users className="h-6 w-6 mx-auto text-blue-500 mb-1" />
              <div className="text-2xl font-bold">{overview?.partners?.total || 0}</div>
              <div className="text-xs text-muted-foreground">New Signups</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <Wallet className="h-6 w-6 mx-auto text-green-500 mb-1" />
              <div className="text-2xl font-bold">{overview?.partners?.with_bank_account || 0}</div>
              <div className="text-xs text-muted-foreground">Complete Bank Details</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <UserCheck className="h-6 w-6 mx-auto text-purple-500 mb-1" />
              <div className="text-2xl font-bold">{overview?.partners?.with_referrals || 0}</div>
              <div className="text-xs text-muted-foreground">With Referrals</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <Activity className="h-6 w-6 mx-auto text-orange-500 mb-1" />
              <div className="text-2xl font-bold">{overview?.partners?.with_transacting_referrals || 0}</div>
              <div className="text-xs text-muted-foreground">Transacting</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <Users className="h-6 w-6 mx-auto text-indigo-500 mb-1" />
              <div className="text-2xl font-bold">{overview?.referrals?.total || 0}</div>
              <div className="text-xs text-muted-foreground">Total Referrals</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <ShoppingCart className="h-6 w-6 mx-auto text-teal-500 mb-1" />
              <div className="text-2xl font-bold">{overview?.orders?.total || 0}</div>
              <div className="text-xs text-muted-foreground">Total Orders</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <DollarSign className="h-6 w-6 mx-auto text-emerald-500 mb-1" />
              <div className="text-2xl font-bold">{formatRevenue(overview?.revenue?.total || 0)}</div>
              <div className="text-xs text-muted-foreground">Total Revenue</div>
            </div>
          </div>
          {overview?.last_aggregate_update && (
            <p className="text-xs text-muted-foreground mt-3 text-right">
              Historical data last updated: {new Date(overview.last_aggregate_update).toLocaleDateString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            {/* Date Range Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Time Period
              </label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[180px]" data-testid="date-filter">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="quarter">Last 3 Months</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Partner Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Partner
              </label>
              <Select value={selectedPartner} onValueChange={setSelectedPartner}>
                <SelectTrigger className="w-[220px]" data-testid="partner-filter">
                  <SelectValue placeholder="Select partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {partners.map((partner) => (
                    <SelectItem key={partner.user_id} value={partner.user_id}>
                      {partner.name} ({partner.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reset Button */}
            {(dateRange !== 'all' || selectedPartner !== 'all') && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-muted-foreground">
                Clear Filters
              </Button>
            )}
          </div>
          
          {/* Active Filters Indicator */}
          {(dateRange !== 'all' || selectedPartner !== 'all') && (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <span>Showing data for:</span>
              {dateRange !== 'all' && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded-md text-xs font-medium">
                  {dateRange === 'today' ? 'Today' : 
                   dateRange === 'week' ? 'Last 7 Days' :
                   dateRange === 'month' ? 'Last 30 Days' :
                   dateRange === 'quarter' ? 'Last 3 Months' : 'Last Year'}
                </span>
              )}
              {selectedPartner !== 'all' && (
                <span className="px-2 py-1 bg-blue-500/10 text-blue-600 rounded-md text-xs font-medium">
                  {partners.find(p => p.user_id === selectedPartner)?.name || 'Selected Partner'}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="hover:shadow-md transition-all duration-200">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </CardTitle>
                    <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`h-5 w-5 ${stat.color}`} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stat.value}
                    </div>
                    <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Partner Breakdown by Date Range */}
      <Card data-testid="partner-breakdown-card">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-500" />
                Partner Breakdown
              </CardTitle>
              <CardDescription>
                Per-partner revenue, commissions, bank-details status & referral split for the selected date range.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={breakdownRange} onValueChange={setBreakdownRange}>
                <SelectTrigger className="w-[160px]" data-testid="breakdown-range-select">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mtd">Current Month</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="quarter">Last 90 Days</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              {breakdownRange === 'custom' && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={breakdownStart}
                    onChange={(e) => setBreakdownStart(e.target.value)}
                    className="w-[140px]"
                    data-testid="breakdown-start-date"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={breakdownEnd}
                    onChange={(e) => setBreakdownEnd(e.target.value)}
                    className="w-[140px]"
                    data-testid="breakdown-end-date"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 px-3 py-1.5 border rounded-md">
                <Label htmlFor="dormant-days" className="text-xs whitespace-nowrap cursor-pointer">Dormant after</Label>
                <Input
                  id="dormant-days"
                  type="number"
                  min="1"
                  max="365"
                  value={dormantDays}
                  onChange={(e) => setDormantDays(parseInt(e.target.value) || 60)}
                  className="w-[72px] h-8"
                  data-testid="breakdown-dormant-days"
                />
                <span className="text-xs text-muted-foreground">days</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 border rounded-md">
                <Switch
                  checked={onlyWithRevenue}
                  onCheckedChange={setOnlyWithRevenue}
                  data-testid="breakdown-only-revenue-toggle"
                  id="only-revenue"
                />
                <Label htmlFor="only-revenue" className="text-xs cursor-pointer">Revenue &gt; 0 only</Label>
              </div>
              <Button
                onClick={handleExportBreakdown}
                disabled={breakdownExporting || breakdownLoading}
                size="sm"
                variant="outline"
                data-testid="breakdown-export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {breakdownExporting ? 'Exporting…' : 'Excel'}
              </Button>
              <Button
                onClick={handleExportBreakdownCsv}
                disabled={breakdownExportingCsv || breakdownLoading}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="breakdown-export-csv-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {breakdownExportingCsv ? 'Exporting…' : 'CSV (per referral)'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary chips */}
          {breakdown?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <div className="text-xs text-muted-foreground">Partners</div>
                <div className="text-xl font-semibold" data-testid="bd-summary-partners">{breakdown.summary.total_partners}</div>
              </div>
              <div className="p-3 rounded-lg bg-green-50 border border-green-100">
                <div className="text-xs text-muted-foreground">Revenue</div>
                <div className="text-xl font-semibold" data-testid="bd-summary-revenue">{formatRevenue(breakdown.summary.total_revenue)}</div>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                <div className="text-xs text-muted-foreground">Commission</div>
                <div className="text-xl font-semibold" data-testid="bd-summary-commission">{formatRevenue(breakdown.summary.total_commission)}</div>
              </div>
              <div className="p-3 rounded-lg bg-teal-50 border border-teal-100">
                <div className="text-xs text-muted-foreground">Avg Order Value</div>
                <div className="text-xl font-semibold" data-testid="bd-summary-aov">{formatRevenue(breakdown.summary.average_order_value || 0)}</div>
              </div>
              <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                <div className="text-xs text-muted-foreground">Top Product</div>
                <div className="text-sm font-semibold truncate" title={breakdown.summary.top_product || 'N/A'} data-testid="bd-summary-top-product">
                  {breakdown.summary.top_product || <span className="text-muted-foreground font-normal italic">N/A</span>}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-100">
                <div className="text-xs text-muted-foreground">With Bank Details</div>
                <div className="text-xl font-semibold">
                  {breakdown.summary.partners_with_bank}<span className="text-sm text-muted-foreground"> / {breakdown.summary.total_partners}</span>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                  Earned, No Bank
                </div>
                <div className="text-xl font-semibold text-red-600" data-testid="bd-summary-earned-no-bank">
                  {breakdown.summary.partners_with_revenue_no_bank}
                </div>
              </div>
            </div>
          )}

          {/* Search box */}
          {breakdown?.rows && breakdown.rows.length > 0 && (
            <div className="mb-3 flex items-center gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by partner name, email, or referral code…"
                  value={breakdownSearch}
                  onChange={(e) => setBreakdownSearch(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="breakdown-search"
                />
                {breakdownSearch && (
                  <button
                    type="button"
                    onClick={() => setBreakdownSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="breakdown-search-clear"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {breakdownSearch && (
                <span className="text-xs text-muted-foreground">
                  {filteredBreakdownRows.length} of {breakdown.rows.length} partners
                </span>
              )}
            </div>
          )}

          {/* Table */}
          {breakdownLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !breakdown?.rows || breakdown.rows.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No partners match the selected filters.
            </div>
          ) : filteredBreakdownRows.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No partners match "{breakdownSearch}".
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table data-testid="partner-breakdown-table">
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Partner</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">AOV</TableHead>
                    <TableHead>Top Product</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead className="text-center">Transacting</TableHead>
                    <TableHead className="text-center">Dormant</TableHead>
                    <TableHead className="text-center">Pending</TableHead>
                    <TableHead className="text-center">Bank</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBreakdownRows.map((r) => {
                    const isOpen = expandedPartner === r.partner_id;
                    const earnedNoBank = r.revenue > 0 && !r.bank_details.has;
                    const pendingCount = Math.max(0, (r.non_transacting_referrals?.count || 0) - (r.dormant_referrals?.count || 0));
                    return (
                      <Fragment key={r.partner_id}>
                        <TableRow
                          className={`cursor-pointer hover:bg-muted/30 ${earnedNoBank ? 'bg-red-50/40' : ''}`}
                          onClick={() => setExpandedPartner(isOpen ? null : r.partner_id)}
                          data-testid={`bd-row-${r.partner_id}`}
                        >
                          <TableCell>
                            {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{r.name}</div>
                            <div className="text-xs text-muted-foreground">{r.email}</div>
                          </TableCell>
                          <TableCell className="text-right font-medium">{formatRevenue(r.revenue)}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {r.transactions_count > 0 ? formatRevenue(r.aov) : <span className="text-xs">—</span>}
                          </TableCell>
                          <TableCell className="text-xs max-w-[180px] truncate" title={r.top_product || ''}>
                            {r.top_product || <span className="text-muted-foreground italic">—</span>}
                          </TableCell>
                          <TableCell className="text-right">{formatRevenue(r.commission)}</TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                              {r.transacting_referrals.count}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium ${r.dormant_referrals.count > 0 ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>
                              {r.dormant_referrals.count}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                              {pendingCount}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {r.bank_details.has ? (
                              <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                                <CheckCircle2 className="h-4 w-4" /> Added
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                                <AlertCircle className="h-4 w-4" /> Missing
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow className="bg-muted/10" data-testid={`bd-detail-${r.partner_id}`}>
                            <TableCell></TableCell>
                            <TableCell colSpan={9}>
                              <div className="grid md:grid-cols-4 gap-6 py-3 px-1">
                                <div>
                                  <div className="text-xs font-semibold text-green-700 mb-2">
                                    Transacting ({r.transacting_referrals.count})
                                  </div>
                                  {r.transacting_referrals.names.length === 0 ? (
                                    <div className="text-xs text-muted-foreground italic">None</div>
                                  ) : (
                                    <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                                      {r.transacting_referrals.names.map((n, i) => (
                                        <li key={i} className="text-foreground">• {n}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                                <div>
                                  <div className="text-xs font-semibold text-rose-700 mb-2">
                                    Dormant &gt; {breakdown.summary.dormant_days}d ({r.dormant_referrals.count})
                                  </div>
                                  {r.dormant_referrals.names.length === 0 ? (
                                    <div className="text-xs text-muted-foreground italic">None</div>
                                  ) : (
                                    <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                                      {r.dormant_referrals.names.map((n, i) => (
                                        <li key={i} className="text-foreground">• {n}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                                <div>
                                  <div className="text-xs font-semibold text-gray-700 mb-2">
                                    Pending (≤ {breakdown.summary.dormant_days}d) ({pendingCount})
                                  </div>
                                  {pendingCount === 0 ? (
                                    <div className="text-xs text-muted-foreground italic">None</div>
                                  ) : (
                                    <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                                      {r.non_transacting_referrals.names
                                        .filter((n) => !r.dormant_referrals.names.includes(n))
                                        .map((n, i) => (
                                          <li key={i} className="text-foreground">• {n}</li>
                                        ))}
                                    </ul>
                                  )}
                                </div>
                                <div>
                                  <div className="text-xs font-semibold text-blue-700 mb-2">Bank & Stats</div>
                                  {r.bank_details.has ? (
                                    <div className="text-xs space-y-1">
                                      <div><span className="text-muted-foreground">Bank:</span> {r.bank_details.bank_name}</div>
                                      <div><span className="text-muted-foreground">Holder:</span> {r.bank_details.account_holder}</div>
                                      <div><span className="text-muted-foreground">A/C:</span> ****{(r.bank_details.account_number || '').slice(-4)}</div>
                                      <div><span className="text-muted-foreground">IFSC:</span> {r.bank_details.ifsc_code}</div>
                                    </div>
                                  ) : (
                                    <div className="text-xs text-red-600">
                                      ⚠️ Missing bank details. {r.revenue > 0 && 'Earned commission cannot be paid out.'}
                                    </div>
                                  )}
                                  <div className="mt-3 text-xs text-muted-foreground space-y-0.5">
                                    <div>Tier: <span className="capitalize">{r.tier || 'N/A'}</span></div>
                                    <div>{r.transactions_count} txn{r.transactions_count !== 1 ? 's' : ''} · AOV {formatRevenue(r.aov)}</div>
                                    {r.top_product && <div className="truncate" title={r.top_product}>Top: {r.top_product}</div>}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>System Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-green-500" />
                <span className="font-medium">Partner Portal</span>
              </div>
              <span className="text-sm text-green-500">Operational</span>
            </div>
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-green-500" />
                <span className="font-medium">Shopify Integration</span>
              </div>
              <span className="text-sm text-green-500">Connected</span>
            </div>
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-green-500" />
                <span className="font-medium">Payment System</span>
              </div>
              <span className="text-sm text-green-500">Active</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
